import { useState } from 'react'
import { IDS_PERFIL_VITIMA, perfilVitimaPorId } from '../dominio/didatico/perfisVitima'
import { CORPOS_SOLTOS, IDS_CORPO_SOLTO, corpoSoltoPorId, type OrigemCarga } from '../dominio/didatico/cargaSolta'
import { compartimentosDeCarga } from '../dominio/didatico/cargaSoltaColocacao'
import { ancorasLivres, type EstadoClinico, type Ocupante, type TipoCorpo } from '../dominio/didatico/ocupantes'
import type { ModoRetencao } from '../dominio/didatico/retencaoOcupante'
import type { PerfilVeiculo } from '../dominio/didatico/perfilVeiculo'

interface Props {
  perfil: PerfilVeiculo
  ocupantes: readonly Ocupante[]
  idFoco: string
  aoAdicionar: (ocupante: Ocupante) => void
  aoRemover: (id: string) => void
  aoAlterarRetencao: (id: string, retencao: ModoRetencao) => void
  aoFocar: (id: string) => void
  modoClinico?: boolean
  aoAlterarEstadoClinico?: (id: string, estado: EstadoClinico) => void
}

let contadorLocal = 0
/** Id só precisa ser único NESTA sessão de UI: o domínio nunca gera id sozinho. */
function novoId(prefixo: string): string {
  contadorLocal += 1
  return `${prefixo}-${contadorLocal}`
}

/**
 * Quem está no veículo: pessoas (com ou sem cinto), animais e objetos soltos.
 *
 * O painel só monta um `Ocupante` e manda para cima — toda regra de "cabe ou
 * não cabe" (uma pessoa por âncora, carga solta sem disputa) vive em
 * `ocupantes.ts` e já foi decidida antes de chegar aqui.
 */
export function PainelOcupantes({ perfil, ocupantes, idFoco, aoAdicionar, aoRemover, aoAlterarRetencao, aoFocar, modoClinico = false, aoAlterarEstadoClinico }: Props) {
  const [tipoNovo, setTipoNovo] = useState<TipoCorpo>('pessoa')
  const livres = ancorasLivres(perfil, ocupantes)
  const [idAncoraNova, setIdAncoraNova] = useState(livres[0]?.id ?? '')
  const [idPerfilVitimaNovo, setIdPerfilVitimaNovo] = useState<string>(IDS_PERFIL_VITIMA[0]!)
  const [idCorpoNovo, setIdCorpoNovo] = useState(IDS_CORPO_SOLTO[0]!)
  // nulo = usar o compartimento que o catálogo declara para aquele corpo
  const [compartimentoNovo, setCompartimentoNovo] = useState<OrigemCarga | null>(null)
  const compartimentos = compartimentosDeCarga(perfil)

  const ancoraAtual = livres.find((a) => a.id === idAncoraNova) ?? livres[0] ?? null

  const adicionar = () => {
    if (tipoNovo === 'pessoa') {
      if (!ancoraAtual) return
      aoAdicionar({
        id: novoId('ocupante'),
        tipo: 'pessoa',
        nome: `${perfilVitimaPorId(idPerfilVitimaNovo).nome} (${ancoraAtual.nome})`,
        idAncora: ancoraAtual.id,
        idPerfil: idPerfilVitimaNovo,
        retencao: 'comCinto',
        estadoClinico: 'estavel',
      })
      return
    }
    // a âncora ainda serve de referência para as superfícies que a carga pode
    // encontrar, mas o PONTO DE PARTIDA agora é o compartimento — antes tudo
    // nascia na âncora do motorista, sobreposto à vítima
    const referencia = perfil.ancoras[0]
    if (!referencia) return
    const corpo = corpoSoltoPorId(idCorpoNovo)
    aoAdicionar({
      id: novoId('carga'),
      tipo: tipoNovo,
      nome: corpo.nome,
      idAncora: referencia.id,
      idPerfil: idCorpoNovo,
      retencao: 'semCinto',
      compartimento: compartimentoNovo ?? corpo.origemPadrao,
    })
  }

  return (
    <div className="grupo-controle">
      <span className="titulo-controle">{modoClinico ? 'Vítimas e prioridade' : 'Quem está no veículo'}</span>

      <ul className="lista-ocupantes">
        {ocupantes.filter((o) => !modoClinico || o.tipo === 'pessoa').map((o) => (
          <li key={o.id} className={o.id === idFoco ? 'ativo' : ''}>
            <div className="ocupante-info">
              <strong>{o.nome}</strong>
              <span className="etiqueta">{o.tipo}</span>
            </div>
            <div className="ocupante-acoes">
              {modoClinico && o.tipo === 'pessoa' && (
                <select
                  aria-label={`Estado de ${o.nome}`}
                  value={o.estadoClinico ?? 'estavel'}
                  onChange={(e) => aoAlterarEstadoClinico?.(o.id, e.target.value as EstadoClinico)}
                >
                  <option value="grave">grave — prioridade clínica</option>
                  <option value="estavel">estável</option>
                </select>
              )}
              {!modoClinico && o.tipo === 'pessoa' && (
                <label className="toggle">
                  <input
                    type="checkbox"
                    checked={o.retencao === 'semCinto'}
                    onChange={(e) => aoAlterarRetencao(o.id, e.target.checked ? 'semCinto' : 'comCinto')}
                  />
                  sem cinto
                </label>
              )}
              <button className={o.id === idFoco ? 'ativo' : ''} onClick={() => aoFocar(o.id)}>
                focar
              </button>
              {ocupantes.length > 1 && (
                <button onClick={() => aoRemover(o.id)} aria-label={`Remover ${o.nome}`}>
                  remover
                </button>
              )}
            </div>
          </li>
        ))}
      </ul>

      {!modoClinico && <div className="linha-botoes">
        <button className={tipoNovo === 'pessoa' ? 'ativo' : ''} onClick={() => setTipoNovo('pessoa')}>
          pessoa
        </button>
        <button className={tipoNovo === 'animal' ? 'ativo' : ''} onClick={() => setTipoNovo('animal')}>
          animal
        </button>
        <button className={tipoNovo === 'objeto' ? 'ativo' : ''} onClick={() => setTipoNovo('objeto')}>
          objeto
        </button>
      </div>}

      {modoClinico || tipoNovo === 'pessoa' ? (
        <>
          <label className="campo">
            <span>posição</span>
            <select value={idAncoraNova} onChange={(e) => setIdAncoraNova(e.target.value)} disabled={livres.length === 0}>
              {livres.length === 0 && <option value="">nenhuma âncora livre</option>}
              {livres.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.nome}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>vítima</span>
            <select value={idPerfilVitimaNovo} onChange={(e) => setIdPerfilVitimaNovo(e.target.value)}>
              {IDS_PERFIL_VITIMA.map((id) => (
                <option key={id} value={id}>
                  {perfilVitimaPorId(id).nome}
                </option>
              ))}
            </select>
          </label>
        </>
      ) : (
        <>
          <label className="campo">
            <span>{tipoNovo === 'animal' ? 'animal' : 'objeto'}</span>
            <select
              value={idCorpoNovo}
              onChange={(e) => {
                setIdCorpoNovo(e.target.value)
                // trocar de objeto volta ao lugar que o catálogo declara para ele
                setCompartimentoNovo(null)
              }}
            >
              {IDS_CORPO_SOLTO.filter((id) => CORPOS_SOLTOS[id]!.tipo === tipoNovo).map((id) => (
                <option key={id} value={id}>
                  {corpoSoltoPorId(id).nome}
                </option>
              ))}
            </select>
          </label>
          <label className="campo">
            <span>onde está</span>
            <select
              value={compartimentoNovo ?? corpoSoltoPorId(idCorpoNovo).origemPadrao}
              onChange={(e) => setCompartimentoNovo(e.target.value as OrigemCarga)}
            >
              {compartimentos.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.nome}
                </option>
              ))}
            </select>
          </label>
        </>
      )}

      <button onClick={adicionar} disabled={tipoNovo === 'pessoa' && livres.length === 0}>
        Adicionar
      </button>
      <p className="dica">
        {modoClinico
          ? 'Graves aparecem primeiro como orientação clínica, sem engessar a ordem. Foque qualquer vítima; as demais permanecem na cena e podem ocupar o corredor ou precisar sair antes para liberar acesso.'
          : 'Sem cinto (ou solto), o corpo continua com a velocidade que tinha até encontrar alguma superfície do habitáculo — não é escolha, é consequência do tipo de colisão selecionado. Cada objeto parte do compartimento onde estava, e dois objetos no mesmo lugar são acomodados lado a lado.'}
      </p>
    </div>
  )
}
